<script lang="ts">
	import {
		ProjectViewDialog,
		type OutputObjectPromptVariantsRequest,
		type OutputObjectPromptVariantsResponse,
		type OutputObjectPrompts,
		type ProjectViewModel,
		type ConstraintSetIds
	} from '@repo/commons/models'
	import {
		Button,
		ButtonSize,
		ButtonType,
		Chip,
		Icon,
		MarkdownInput
	} from '@repo/commons/components'
	import {
		type SupportedLanguage as SupportedLanguage,
		Modality
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import {
		OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG,
		replaceNewlinesWithSpaces,
		getConstraintNamesFromKey
	} from '@repo/commons/utils'

	interface Props {
		project: ProjectViewModel
		promptVariantsRequest: OutputObjectPromptVariantsRequest
		onPromptVariantsResponse: (response: OutputObjectPromptVariantsResponse) => void
		onClose?: () => void
	}

	let { project, promptVariantsRequest, onPromptVariantsResponse, onClose }: Props = $props()

	const language = promptVariantsRequest.language
	const selectedModality: Modality = promptVariantsRequest.selectedModality

	const constraints = $derived(project?.constraints || [])

	/** Build a deep copy of prompts from the request without mutating the prop. */
	function buildLocalPromptsCopy(request: OutputObjectPromptVariantsRequest): OutputObjectPrompts {
		const result: OutputObjectPrompts = {}
		for (const key in request.prompts) {
			const byModality = request.prompts[key as ConstraintSetIds][selectedModality] ?? {}
			result[key as ConstraintSetIds] = {
				...(request.prompts[key as ConstraintSetIds] as Record<
					Modality,
					Record<SupportedLanguage, string[]>
				>),
				[selectedModality]: {
					...byModality,
					[language]: Array.isArray(byModality[language]) ? [...byModality[language]] : ['']
				}
			}
		}
		return result
	}

	let localPrompts = $state<OutputObjectPrompts>(buildLocalPromptsCopy(promptVariantsRequest))

	$effect(() => {
		if (promptVariantsRequest) {
			const req = promptVariantsRequest
			localPrompts = buildLocalPromptsCopy(req)
		}
	})

	const handleInput = (key: string, index: number, value: string) => {
		const k = key as ConstraintSetIds
		if (!localPrompts[k][selectedModality]) {
			localPrompts[k][selectedModality] = {} as Record<SupportedLanguage, string[]>
		}
		if (!localPrompts[k][selectedModality][language]) {
			localPrompts[k][selectedModality][language] = ['']
		}
		const arr = [...localPrompts[k][selectedModality][language]]
		arr[index] = value
		localPrompts[k][selectedModality][language] = arr
	}

	const handleBlur = (_key: string, _index: number) => {
		// Mark field as touched, etc.
	}

	const handleAddVariant = (key: string) => {
		const k = key as ConstraintSetIds
		if (!localPrompts[k][selectedModality]) {
			localPrompts[k][selectedModality] = {} as Record<SupportedLanguage, string[]>
		}
		if (!localPrompts[k][selectedModality][language]) {
			localPrompts[k][selectedModality][language] = ['']
		}
		localPrompts[k][selectedModality][language] = [
			...localPrompts[k][selectedModality][language],
			''
		]
	}

	const handleSubmit = () => {
		const promptsToSend: OutputObjectPrompts = {} as OutputObjectPrompts
		for (const key in localPrompts) {
			const k = key as ConstraintSetIds
			promptsToSend[k] = { ...localPrompts[k] } as Record<
				Modality,
				Record<SupportedLanguage, string[]>
			>
			if (promptsToSend[k][selectedModality]?.[language]) {
				promptsToSend[k][selectedModality][language] = localPrompts[k][selectedModality][
					language
				].filter((variant) => variant.trim() !== '')
			}
		}
		onPromptVariantsResponse({
			prompts: promptsToSend,
			selectedConstraintIds: promptVariantsRequest.selectedConstraintIds,
			language: promptVariantsRequest.language,
			selectedModality: promptVariantsRequest.selectedModality,
			dialogType: ProjectViewDialog.OUTPUT_OBJECT
		} as OutputObjectPromptVariantsResponse)
	}

	const handlePromptDeleted = (key: string, index: number) => {
		if (index > 0) {
			const k = key as ConstraintSetIds
			const arr = localPrompts[k][selectedModality][language].filter((_, i) => i !== index)
			localPrompts[k][selectedModality][language] = arr
		}
	}

	const getVariantTitle = (index: number) => {
		return index === 0 ? 'Default' : `Variant ${index}`
	}
</script>

<div class="container">
	{#if localPrompts && localPrompts[promptVariantsRequest.selectedConstraintIds as ConstraintSetIds] && localPrompts[promptVariantsRequest.selectedConstraintIds as ConstraintSetIds][selectedModality]}
		<div class="form">
			{#each localPrompts[promptVariantsRequest.selectedConstraintIds as ConstraintSetIds][selectedModality][language] as variant, index (index)}
				<div class="variant">
					<div class="variant-header">
						<p class="variant-title">{getVariantTitle(index)}</p>
						<Button
							buttonType={ButtonType.TERTIARY_INVERSE}
							buttonSize={ButtonSize.EXTRA_SMALL}
							rounded
							disabled={index === 0}
							iconProps={{ name: 'trash', size: 'sm', class: 'text-[#002C4D80]' }}
							onclick={() =>
								handlePromptDeleted(promptVariantsRequest.selectedConstraintIds, index)}
						/>
					</div>
					{#if selectedModality === Modality.Voice}
						{@const chips =
							getConstraintNamesFromKey(constraints, promptVariantsRequest.selectedConstraintIds) ||
							[]}
						<MarkdownInput
							toolbarConfig={OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG}
							value={replaceNewlinesWithSpaces(variant)}
							allowLineBreaks={false}
							onInput={(value: string) =>
								handleInput(
									promptVariantsRequest.selectedConstraintIds,
									index,
									replaceNewlinesWithSpaces(value)
								)}
							onBlur={() => handleBlur(promptVariantsRequest.selectedConstraintIds, index)}
						>
							<div class="bottom-row">
								{#if chips}
									<div class="chips-container">
										{#each chips as chip}
											<Chip text={chip} removable={false} />
										{/each}
									</div>
								{/if}
							</div>
						</MarkdownInput>
					{:else}
						<MarkdownInput
							value={variant || ''}
							enableImageUpload={true}
							onInput={(value: string) =>
								handleInput(promptVariantsRequest.selectedConstraintIds, index, value)}
							onBlur={() => handleBlur(promptVariantsRequest.selectedConstraintIds, index)}
						/>
					{/if}
				</div>
			{/each}
			<div class="add-variant">
				<Button
					buttonType={ButtonType.NEUTRAL}
					buttonText="Add New Variant"
					fillWidth={true}
					onclick={() => handleAddVariant(promptVariantsRequest.selectedConstraintIds)}
				/>
			</div>
		</div>
	{/if}

	<div class="submit-container">
		<Button
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			onclick={handleSubmit}
			buttonText="Submit"
		/>
		<Button
			buttonText="Cancel"
			buttonType={ButtonType.TERTIARY_INVERSE}
			buttonSize={ButtonSize.LARGE}
			onclick={onClose}
		/>
	</div>
</div>

<style lang="postcss">
	.container {
		@apply w-full mx-auto;
	}

	.form {
		@apply flex flex-col gap-6 mb-8;
	}

	.variant {
		@apply flex flex-col gap-1;
	}

	.variant-header {
		@apply flex justify-between items-center gap-2;
	}

	.variant-title {
		@apply text-base;
	}

	.add-variant {
		@apply block;
	}

	.submit-container {
		@apply mt-4 flex gap-4;
	}

	.bottom-row {
		@apply flex justify-between items-start border-t border-[#002C4D80]/20 bg-gray-50 px-2 py-4;
	}

	.chips-container {
		@apply flex flex-wrap gap-2;
		max-width: 70%;
	}
</style>
